import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <SEOHead title="文章未找到" description="您访问的文章不存在或已被移除。" />
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-6xl text-foreground-300"></i>
            <h1 className="mt-4 text-2xl font-bold text-foreground-800 font-heading">文章未找到</h1>
            <p className="mt-2 text-foreground-500">您访问的文章不存在或已被移除。</p>
            <Link to="/" className="mt-6 inline-block px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料社区网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliao.shequ.cn/article/${article.id}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliao.shequ.cn/' },
      { '@type': 'ListItem', position: 2, name: category?.name || article.category, item: `https://heiliao.shequ.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://heiliao.shequ.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        publishedTime={article.publishDate}
        articleTags={article.tags}
      />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || article.category, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {category?.name || article.category}
                </Link>
                {article.hot && (
                  <span className="px-3 py-1 text-xs font-bold bg-accent-500 text-white rounded-full whitespace-nowrap">
                    HOT
                  </span>
                )}
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-1 text-xs bg-background-100 text-foreground-500 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4 font-heading">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万阅读` : `${article.readCount}阅读`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {article.commentCount}评论
                </span>
              </div>
            </header>

            <figure className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </figure>

            <div
              className="prose prose-lg max-w-none mb-10
                prose-headings:font-heading prose-headings:text-foreground-900
                prose-p:text-foreground-700 prose-p:leading-relaxed
                prose-blockquote:border-l-primary-500 prose-blockquote:bg-background-100 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-blockquote:text-foreground-600
                prose-strong:text-foreground-900
                [&_h4]:text-lg [&_h4]:font-bold [&_h4]:mt-8 [&_h4]:mb-3"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center justify-between py-6 border-t border-b border-background-200 mb-10">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors duration-200 text-sm cursor-pointer whitespace-nowrap">
                  <i className="ri-thumb-up-line"></i>
                  点赞 ({article.likes >= 10000 ? `${(article.likes / 10000).toFixed(1)}万` : article.likes})
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors duration-200 text-sm cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
              <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-500 transition-colors duration-200 text-sm cursor-pointer whitespace-nowrap">
                <i className="ri-bookmark-line"></i>
                收藏
              </button>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mb-10">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-6 h-6 rounded bg-secondary-500 flex items-center justify-center">
                  <i className="ri-links-line text-white text-xs"></i>
                </div>
                <h2 className="font-heading text-xl font-bold text-foreground-800">相关推荐</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}